<template>
	<Row @click.native="toggleIsOpen" class="shared-group-container width-100 flex-align-center flex-justify-space-between share-list-item">
		<Row class="flex-align-center">
			<ShareFeatureItemAvatarAndName isGroup :name="group.name" :item="group" />
			<v-icon :class="{ rotate: isOpen }" class="chevron ml-2">mdi-chevron-right</v-icon>
			<h5 class="num-of-users-un-group show-on-hover reg ml-3">{{ groupHoverDescription }}</h5>
		</Row>
		<Row class="flex-align-center">
			<h5 v-if="hideVisibilityStatus" @click="removeAccess" class="link show-on-hover reg mr-2">Remove access</h5>
			<h3 v-else class="visibility-state-text show-on-hover reg ml-4">{{ visibilityStatus }} by default</h3>
			<GroupFeatureVisibilityDropdown
				v-if="!hideVisibilityStatus"
				class="visibility-dropdown ml-5"
				v-bind="groupFeatureVisibilityDropdownProps"
				v-on="groupFeatureVisibilityDropdownListeners"
			/>
		</Row>
	</Row>
</template>
<script>
import { WorkflowAccessType } from '@ply-io/ply-front-shared-utilities';

import GroupFeatureVisibilityDropdown from '@/components/FeatureVisibility/GroupFeatureVisibilityDropdown.vue';
import ShareFeatureItemAvatarAndName from '@/components/dialogs/ShareFeatureDialog/ShareFeatureItemAvatarAndName.vue';
import { OPTIONS_MAP, EVERYONE_GROUP_ID } from '@/components/dialogs/ShareModuleDialog/share-module-dialog.const.js';
import { isLockedShareEnabled } from '@/store/modules/featureFlags/getters';
import {
	updateWorkflow,
	updateWorkflowGroupRole,
	toggleWorkflowIsOnSettings,
	changeGroupFeatureVisibility,
	applyExistingWorkflowVisibilityToAllGroupMembers,
} from '@/store/modules/workflows/actions';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { pluralize } from '@/utils/language-service';

export default {
	name: 'SharedGroupItemHeader',
	components: {
		ShareFeatureItemAvatarAndName,
		GroupFeatureVisibilityDropdown,
	},
	props: {
		group: { type: Object },
		isOpen: { type: Boolean },
		workflow: { type: Object },
		isLocked: { type: Boolean },
		isVisible: { type: Boolean },
		usersSharedData: { type: Array },
		hideVisibilityStatus: { type: Boolean },
		loadingVisibilityChange: { type: Boolean },
		isNestedInsideAnotherGroup: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ isLockedShareEnabled }),
		groupFeatureVisibilityDropdownProps() {
			return {
				isLocked: this.isLocked,
				isVisible: this.isVisible,
				isLoading: this.loadingVisibilityChange,
				showRemoveAccessOnly: this.isEveryoneGroup,
				allowRemoveAccess: !this.isNestedInsideAnotherGroup,
				allowLockVisibility: !this.isNestedInsideAnotherGroup && !this.isEveryoneGroup,
			};
		},
		groupFeatureVisibilityDropdownListeners() {
			return {
				'remove-access': this.removeAccess,
				// NEW USERS DEFAULT
				'set-visible-locked-for-new-users': this.setVisibleLockedForNewUsers,
				'set-hidden-for-new-users': () => this.setGroupVisibility({ isOn: false, applyForCurrent: false }),
				'set-visible-for-new-users': () => this.setGroupVisibility({ isOn: true, applyForCurrent: false }),
				// CURRENT USERS DEFAULT
				'set-visible-locked-for-current-users': this.setVisibleLockedForCurrentUsers,
				'set-hidden-for-current-users': () => this.setGroupVisibility({ isOn: false, applyForCurrent: true }),
				'set-visible-for-current-users': () => this.setGroupVisibility({ isOn: true, applyForCurrent: true }),
			};
		},
		workflowId() {
			return this.workflow.id;
		},
		visibilityStatus() {
			if (this.isLocked) {
				return 'Visible - locked';
			}
			if (this.isVisible) {
				return 'Visible';
			}
			return 'Hidden';
		},
		numOfUsersInGroup() {
			return this.group.users?.length || 0;
		},
		numOfSubGroupsInGroup() {
			return this.group.sub_groups?.length || 0;
		},
		numOfSubGroupsText() {
			return this.pluralizeIfNeeded('sub-group', this.numOfSubGroupsInGroup);
		},
		numOfUsersInGroupText() {
			return this.pluralizeIfNeeded('user', this.numOfUsersInGroup);
		},
		groupHoverDescription() {
			const sentenceDivider = this.numOfSubGroupsInGroup === 0 || this.numOfUsersInGroup === 0 ? '' : ', ';
			return `${this.numOfUsersInGroupText}${sentenceDivider}${this.numOfSubGroupsText}`;
		},
		isEveryoneGroup() {
			return this.group.id === EVERYONE_GROUP_ID;
		},
	},
	methods: {
		...mapActionsShorthand({
			updateWorkflow,
			updateWorkflowGroupRole,
			toggleWorkflowIsOnSettings,
			changeGroupFeatureVisibility,
			applyExistingWorkflowVisibilityToAllGroupMembers,
		}),
		pluralizeIfNeeded(noun, number) {
			return number > 0 ? pluralize(noun, number, true) : '';
		},
		toggleIsOpen() {
			this.$emit('update:isOpen', !this.isOpen);
		},
		async setGroupVisibility({ isOn, applyForCurrent }) {
			if (!applyForCurrent && isOn === this.isVisible) {
				// if apply for current, we want to apply the visibility even if it's the same as the current default one
				return;
			}
			this.updateLoadingState(true);
			try {
				await this.changeGroupFeatureVisibility({
					isOn,
					applyForCurrent,
					groupId: this.group.id,
					workflowId: this.workflowId,
				});
			} catch {
				this.$toast.error('Something went wrong, please try again later');
			} finally {
				this.updateLoadingState(false);
			}
		},
		async setIsLocked(newV) {
			if (this.isLockedShareEnabled) {
				const { workflowId, group } = this;
				const role = newV ? OPTIONS_MAP.CAN_USE_ENFORCED : OPTIONS_MAP.CAN_USE;
				await this.updateGroupRole({ flowId: workflowId, role, groups: [group.id] });
			}
		},
		async setVisible() {
			if (this.isVisible) {
				return;
			} else {
				return this.toggleIsVisible();
			}
		},
		async setHidden() {
			if (!this.isVisible) {
				return;
			} else {
				return this.toggleIsVisible();
			}
		},
		async toggleIsVisible() {
			return this.toggleWorkflowIsOnSettings({ id: this.workflowId, groupId: this.group.id });
		},
		async updateGroupRole(payload) {
			this.updateLoadingState(true);
			try {
				await this.updateWorkflowGroupRole(payload);
			} catch {
				this.$toast.error('Something went wrong, please try again later');
			} finally {
				this.updateLoadingState(false);
			}
		},
		updateLoadingState(isLoading) {
			this.$emit('update:loadingVisibilityChange', isLoading);
		},
		async removeAccess() {
			if (this.isEveryoneGroup) {
				this.removeEveryoneGroup();
			} else {
				this.updateGroupRole({ flowId: this.workflowId, role: null, groups: [this.group.id] });
			}
		},
		async removeEveryoneGroup() {
			this.updateLoadingState(true);
			try {
				await this.updateWorkflow({ data: { access_type: WorkflowAccessType.SharedUsersOnly }, id: this.workflow.id });
			} catch {
				this.$toast.error('Something went wrong, please try again later');
			} finally {
				this.updateLoadingState(false);
			}
		},
		async setVisibleLockedForNewUsers() {
			if (this.isLockedShareEnabled) {
				await this.changeConfig(Promise.all([this.setIsLocked(true), this.setVisible()]));
			}
		},
		async setVisibleLockedForCurrentUsers() {
			if (this.isLockedShareEnabled) {
				await this.setVisibleLockedForNewUsers();
				await this.applyVisibilityToAllCurrentGroupMembers();
			}
		},
		async applyVisibilityToAllCurrentGroupMembers() {
			this.updateLoadingState(true);
			try {
				await this.applyExistingWorkflowVisibilityToAllGroupMembers({ id: this.workflowId, groupId: this.group.id });
			} catch (e) {
				this.$toast.error('We had an issue while updating this group visibility');
				console.error('We had an issue while updating your flow visibility: ', e);
			} finally {
				this.updateLoadingState(false);
			}
		},
		async changeConfig(promise) {
			this.updateLoadingState(true);
			try {
				await promise;
			} catch (e) {
				this.$toast.error('We had an issue while updating the group settings. please try again later');
				console.error('We had an issue while updating your flow visibility: ', e);
			} finally {
				this.updateLoadingState(false);
			}
		},
	},
};
</script>

<style scoped lang="scss">
.shared-group-container {
	cursor: pointer;
	padding: 5px 0px;
	transition: var(--transition-time);
	&:hover {
		background: #f6f8fb;
		transition: var(--transition-time);
		.show-on-hover {
			display: flex;
		}
		::v-deep(.visibility-dropdown) {
			.activator-container {
				transition: var(--transition-time);
				background: #f6f8fb;
			}
		}
	}
}
.setup-required-text {
	opacity: 0.5;
	color: var(--bold-title);
}
.show-on-hover {
	display: none;
}
.visibility-state-text {
	@extend .setup-required-text;
}
::v-deep(.visibility-dropdown) {
	.activator-container {
		border: unset;
		transition: var(--transition-time);
	}
}
.num-of-users-un-group {
	@extend .setup-required-text;
}
.rotate {
	transform: rotate(90deg);
	transition: var(--transition-time);
}
.chevron {
	font-size: 16px;
	color: #64666a;
}
</style>
